import { isUndefined } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { availableForGracePeriod, availableForOutput } from '../../../shared/calculator/constants';
import { Calculator } from '../../../shared/icons';
import { ConfirmationModal } from '../../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { UnsavedChangesModal } from '../../../shared/modal/UnsavedChangesModal';
import { CalculatorFieldTypeUI, getAllCalculatorFieldNames, getAllPadOptions, getCalculatorFieldTypeHasUpdated, getShowCalculatorFieldTypePreview, selectFieldType, toggleCalculatorFieldTypePreview, updateFieldTypeValue, upsertCalculatorFieldTypeStarted } from '../store';
import { SelectedCalculatorFieldType } from './SelectedCalculatorFieldType';
import { CalculatorFieldTypePreview } from './CalculatorFieldTypePreview';

interface CalculatorFieldTypeModalProps {
    calculatorField: CalculatorFieldTypeUI;
}

export const CalculatorFieldTypeModal: React.FC<CalculatorFieldTypeModalProps> = ({ calculatorField }) => {
    const { name, buildPadOptions, outputPadOptions, gracePeriodPadOptions } = calculatorField;
    const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const allPadOptions = useAppSelector(getAllPadOptions);
    const hasUpdated = useAppSelector(getCalculatorFieldTypeHasUpdated);
    const calculatorFieldNames = useAppSelector(getAllCalculatorFieldNames);
    const showPreview = useAppSelector(getShowCalculatorFieldTypePreview);

    const closeModal = useCallback(() => hasUpdated ? setUnsavedChangesModalOpen(true) : dispatch(selectFieldType(null)), [hasUpdated, dispatch, setUnsavedChangesModalOpen]);

    const confirmUnsavedChanges = useCallback(() => {
        setUnsavedChangesModalOpen(false);
        dispatch(selectFieldType(null));
    }, [setUnsavedChangesModalOpen, dispatch]);

    const updateProperty = useCallback((key: keyof CalculatorFieldTypeUI, value: string | number | number[]) => dispatch(updateFieldTypeValue(key, value)), [dispatch]);

    const saveCalculatorFieldType = useCallback(() => dispatch(upsertCalculatorFieldTypeStarted()), [dispatch]);

    const togglePreview = useCallback(() => dispatch(toggleCalculatorFieldTypePreview(!showPreview)), [dispatch, showPreview]);

    const defaultOutputOptions = useMemo(() => allPadOptions.filter(({ inputType }) => (availableForOutput as string[]).includes(inputType)).map(({ calculatorPadOptionId }) => calculatorPadOptionId), [allPadOptions]);
    const defaultGracePeriodOptions = useMemo(() => allPadOptions.filter(({ inputType }) => (availableForGracePeriod as string[]).includes(inputType)).map(({ calculatorPadOptionId }) => calculatorPadOptionId), [allPadOptions]);

    const modalHeader = useMemo(() => !isUndefined(calculatorField.calculatorFieldTypeId) ? capitaliseStartLetter(name) : 'Create Calculator Field Type', [calculatorField.calculatorFieldTypeId, name]);
    const previewLabel = useMemo(() => showPreview ? 'Continue Editing' : 'Preview', [showPreview]);

    const otherCalculatorFieldNames = useMemo(() => calculatorFieldNames.filter(({ calculatorFieldTypeId }) => calculatorFieldTypeId !== calculatorField.calculatorFieldTypeId).map(({ name }) => name.toLowerCase()), [calculatorFieldNames, calculatorField.calculatorFieldTypeId]);
    const nameAlreadyExists = useMemo(() => otherCalculatorFieldNames.includes(name.toLowerCase()), [otherCalculatorFieldNames, name]);

    const confirmDisabled = useMemo(() => !hasUpdated || name.length === 0 || buildPadOptions.length === 0 || nameAlreadyExists, [hasUpdated, name.length, buildPadOptions.length, nameAlreadyExists]);
    const confirmDisabledTooltip = useMemo(() => {
        let disabledTooltip = [];
        if (name.length === 0) {
            disabledTooltip.push('You must provide a name for your calculator field');
        }
        if (buildPadOptions.length === 0) {
            disabledTooltip.push('You must include at least one option in your builder pad options');
        }
        if (!hasUpdated) {
            disabledTooltip.push('You have not made any changes to the calculator field');
        }
        if (nameAlreadyExists) {
            disabledTooltip.push('Calculator field type names must be unique');
        }
        return disabledTooltip;
    }, [hasUpdated, name.length, buildPadOptions.length, nameAlreadyExists]);

    useEffect(() => {
        if (isUndefined(calculatorField.calculatorFieldTypeId) && outputPadOptions.length === 0 && gracePeriodPadOptions.length === 0) {
            updateProperty('outputPadOptions', defaultOutputOptions);
            updateProperty('gracePeriodPadOptions', defaultGracePeriodOptions);
        }
    }, [updateProperty, defaultOutputOptions, defaultGracePeriodOptions, calculatorField.calculatorFieldTypeId, outputPadOptions, gracePeriodPadOptions]);

    const modalContent = useMemo(() => showPreview ? <CalculatorFieldTypePreview calculatorField={calculatorField} /> : <SelectedCalculatorFieldType calculatorField={calculatorField} nameAlreadyExists={nameAlreadyExists} />, [showPreview, calculatorField, nameAlreadyExists]);

    return (
        <>
            <ConfirmationModal
                isOpen
                closeModal={closeModal}
                confirm={saveCalculatorFieldType}
                confirmLabel='Save'
                confirmDisabled={confirmDisabled}
                confirmDisabledTooltip={confirmDisabledTooltip}
                showPreview
                openPreview={togglePreview}
                previewLabel={previewLabel}
            >
                <ModalHeader icon={Calculator} label={modalHeader} />
                {modalContent}
            </ConfirmationModal>
            <UnsavedChangesModal isOpen={unsavedChangesModalOpen} closeModal={() => setUnsavedChangesModalOpen(false)} confirm={confirmUnsavedChanges} />
        </>
    );
};
